import {
  CaretLeft,
  CaretRight,
  ChatCircle,
  FilmStrip,
  Heart,
  ShareFat,
} from '@phosphor-icons/react';
import type { Platform as ApiPlatform } from '@reelplicate/api-client/model';
import { BrandMark, cn, Inline, Stack } from '@reelplicate/ui';
import { useState } from 'react';

import { PLATFORM_NAMES } from '@/features/calendar/platforms';

import type { PickedMedia, PostType } from './form';

export type PreviewAccount = { platform: ApiPlatform; handle: string };

const MARKS = { TIKTOK: 'tiktok', INSTAGRAM: 'instagram', YOUTUBE: 'youtube' } as const;
type Shown = keyof typeof MARKS;
const shown = (p: ApiPlatform): p is Shown => p in MARKS;

/** Instagram cuts a caption at 125 characters; TikTok and Shorts after about 2 lines (CSS). */
const INSTAGRAM_CUT = 125;

/**
 * Figma "Preview" (357:3591): the post on a 270×480 phone - the reel's
 * poster (or the carousel's first slide), the handle and the caption as the
 * platform cuts it, with a switch per platform it goes to.
 */
export function PostPreview({
  accounts,
  platform,
  onPlatformChange,
  type,
  media,
  captionFor,
}: {
  /** The picked accounts, one per platform shown. */
  accounts: PreviewAccount[];
  platform: ApiPlatform | undefined;
  onPlatformChange: (platform: ApiPlatform) => void;
  type: PostType;
  media: PickedMedia[];
  captionFor: (platform: ApiPlatform) => string;
}) {
  const platforms = [...new Set(accounts.map((a) => a.platform))].filter(shown);
  const current =
    platform && platforms.includes(platform as Shown) ? platform : platforms[0];
  const account = accounts.find((a) => a.platform === current);
  const [slide, setSlide] = useState(0);
  // Slides come and go (and move): stay on a slide that exists.
  const index = Math.min(slide, Math.max(0, media.length - 1));
  const carousel = type === 'carousel';
  const first = media[carousel ? index : 0];
  // A slide's own file is the full image; its thumbnail may be cropped.
  const image = carousel ? (first?.fileUrl ?? first?.thumbnailUrl) : first?.thumbnailUrl;
  const caption = current ? captionFor(current).trim() : '';
  const empty = !first;

  return (
    <Stack
      as="aside"
      aria-label="Preview"
      gap={4}
      align="center"
      className="bg-bg-subtle min-w-0 flex-1 overflow-y-auto p-6"
    >
      <Inline gap={2} className="h-8 w-full">
        <h3 className="text-label-md text-text-secondary flex-1">Preview</h3>
        {platforms.length > 1 &&
          platforms.map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={p === current}
              aria-label={`Preview on ${PLATFORM_NAMES[MARKS[p]]}`}
              onClick={() => onPlatformChange(p)}
              className={cn(
                'focus-visible:shadow-focus-ring flex size-8 items-center justify-center rounded-md outline-none',
                p === current
                  ? 'bg-bg-surface border-border-default border'
                  : 'hover:bg-bg-muted',
              )}
            >
              <BrandMark name={MARKS[p]} size={16} />
            </button>
          ))}
      </Inline>

      <div className="bg-bg-inverse relative h-120 w-[270px] shrink-0 overflow-hidden rounded-[32px]">
        <div
          className={cn(
            'absolute inset-2 overflow-hidden rounded-[26px]',
            empty && 'bg-bg-muted flex flex-col items-center justify-center gap-2 px-8',
          )}
        >
          {empty ? (
            <>
              <FilmStrip aria-hidden className="text-icon-secondary size-7" />
              <p className="text-body-sm text-text-secondary text-center">
                Add a {type === 'reel' ? 'reel' : 'carousel'} to see how it looks on each
                account
              </p>
            </>
          ) : (
            <>
              {image ? (
                <img
                  src={image}
                  alt={carousel ? `Slide ${index + 1}: ${first.title}` : ''}
                  // Slides aren't always 9:16: show the whole image, letterboxed.
                  className={cn(
                    'absolute inset-0 size-full',
                    carousel ? 'bg-black object-contain' : 'object-cover',
                  )}
                />
              ) : (
                <video
                  src={first.fileUrl ?? undefined}
                  muted
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 size-full object-cover"
                />
              )}
              <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-[190px] bg-gradient-to-b from-[rgba(38,39,25,0)] to-[rgba(38,39,25,0.85)]"
              />
              {carousel && media.length > 1 && (
                <>
                  <Inline
                    gap={1}
                    align="center"
                    aria-label={`Slide ${index + 1} of ${media.length}`}
                    className="absolute inset-x-0 top-3"
                  >
                    {media.map((m, i) => (
                      <button
                        key={m.id}
                        type="button"
                        aria-label={`Show slide ${i + 1}`}
                        aria-current={i === index || undefined}
                        onClick={() => setSlide(i)}
                        className={cn(
                          'size-1.5 rounded-full',
                          i === index ? 'bg-white' : 'bg-white/50',
                        )}
                      />
                    ))}
                  </Inline>
                  {index > 0 && (
                    <button
                      type="button"
                      aria-label="Previous slide"
                      onClick={() => setSlide(index - 1)}
                      className="focus-visible:shadow-focus-ring absolute top-1/2 left-2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 outline-none"
                    >
                      <CaretLeft aria-hidden className="text-text-primary size-4" />
                    </button>
                  )}
                  {index < media.length - 1 && (
                    <button
                      type="button"
                      aria-label="Next slide"
                      onClick={() => setSlide(index + 1)}
                      className="focus-visible:shadow-focus-ring absolute top-1/2 right-2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 outline-none"
                    >
                      <CaretRight aria-hidden className="text-text-primary size-4" />
                    </button>
                  )}
                </>
              )}
              <Stack
                gap={1.5}
                className="text-text-inverse absolute inset-x-0 bottom-0 pr-14 pb-[18px] pl-3.5"
              >
                {account && <p className="text-label-md">{account.handle}</p>}
                {caption && (
                  <p
                    className={cn(
                      'text-caption whitespace-pre-line',
                      current !== 'INSTAGRAM' && 'line-clamp-2',
                    )}
                  >
                    {current === 'INSTAGRAM' && [...caption].length > INSTAGRAM_CUT
                      ? `${[...caption].slice(0, INSTAGRAM_CUT).join('')}… more`
                      : caption}
                  </p>
                )}
              </Stack>
              <div
                aria-hidden
                className="text-text-inverse absolute top-[300px] left-[218px] flex flex-col items-center gap-[14px]"
              >
                <Heart className="size-6" />
                <ChatCircle className="size-6" />
                <ShareFat className="size-6" />
              </div>
            </>
          )}
        </div>
      </div>

      <p className="text-caption text-text-tertiary text-center">
        {empty || !account
          ? 'Choose accounts and media to preview each post.'
          : 'Captions are cut after about 2 lines on TikTok and 125 characters on Instagram.'}
      </p>
    </Stack>
  );
}
